import {
  MultiDropdownRenderItem,
  ObjectDetails,
  ObjectsManager,
  ObjectsTable,
  SearchBoxButton,
  useMultiDropdownFilter,
} from '@carboncopyco/editor-ui';
import { beautifyTableLabel } from '@carboncopyco/editor-utils';
import { HideOutlineIcon } from '@carboncopyco/icons';
import type {
  TableCraftFunction,
  TableCraftObject,
  TableCraftRow,
} from '@carboncopyco/types';
import { MultiDropdownFilter, TooltipBubble, notify } from '@carboncopyco/ui';
import { memo, useCallback, useEffect, useState } from 'react';
import {
  useEditor,
  useEditorTableCraftData,
} from '../context/EditorSdkProvider';
import { useEditorEvent } from '../hooks/useEditorEvent';
import { useEditorTool } from '../hooks/useEditorTool';
import { type TableCraftObjectsStatus, assistType } from '../types';

const MultiDropdownFilterContainer = ({
  selectedObject,
  onToggleColumn,
  onClearColumns,
}: {
  selectedObject: TableCraftObject;
  onToggleColumn: (columnKey: string) => Promise<void>;
  onClearColumns: () => Promise<void>;
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const entries = (selectedObject?.filter_columns ?? []).map((col) => [
    col,
    true,
  ]);

  const {
    selectedItems: typesFilterSelectedItems,
    onChange: onTypesFilterChange,
    onClear: onTypesFilterClear,
  } = useMultiDropdownFilter({
    fixedGroupItems:
      selectedObject?.columns.map((col) => ({
        id: col,
        label: beautifyTableLabel(col),
      })) ?? [],
    selectedItems: Object.fromEntries(entries),
  });

  return (
    <MultiDropdownFilter
      placeholder="Hidden columns"
      fixedGroup={{
        items: selectedObject.columns.map((col) => ({
          id: col,
          label: beautifyTableLabel(col),
        })),
      }}
      renderItem={(props) => (
        <MultiDropdownRenderItem
          {...props}
          isDisabled={isLoading}
        />
      )}
      selectedItems={typesFilterSelectedItems}
      onChange={async (item) => {
        onTypesFilterChange(item);
        setIsLoading(true);
        onToggleColumn(item).then(() => setIsLoading(false));
      }}
      onClear={async () => {
        onTypesFilterClear();
        setIsLoading(true);
        onClearColumns().then(() => setIsLoading(false));
      }}
    />
  );
};

type CellUpdateData = {
  objectId: string;
  column: string;
  value: string | boolean;
  row_id: string;
  id_column: string;
};

type ColumnUpdateData = {
  objectId: string;
  columnKey: string;
  type: 'AI' | 'hide' | 'set-object-pk' | 'function';
  data: unknown;
};

type UpdateSettingsData = {
  display_name?: string;
  filter_columns?: string[];
  hide?: boolean;
  set_object_pk?: string;
  object_id?: string;
};

const scrollToObject = (objectId: TableCraftObject['structure_sha']) => {
  const element = document.querySelector(
    `[data-rbd-draggable-id="${objectId}"]`
  );

  if (element) {
    element.scrollIntoView({
      behavior: 'smooth',
    });
  }
};

const getNormalizedParams = (params: Record<string, boolean | string>) => {
  return Object.keys(params).reduce((acc, key) => {
    if (key === 'reference_datetime') {
      acc[key] = params[key]
        ? new Date(params[key] as string).toISOString()
        : '';
      return acc;
    }

    acc[key] = params[key];
    return acc;
  }, {});
};

const excludeKeys = (obj: Record<string, unknown>, keysToExclude: string[]) => {
  return Object.keys(obj)
    .filter((key) => !keysToExclude.includes(key))
    .reduce((acc, key) => {
      acc[key] = obj[key];
      return acc;
    }, {});
};

const getFilterColumns = (columns: string[], columnKey: string) =>
  columns.includes(columnKey)
    ? columns.filter((column) => column !== columnKey)
    : [...columns, columnKey];

export const ObjectsTool = memo(() => {
  const { isOpen, close, open } = useEditorTool('objectsManager');
  const editorSdk = useEditor();
  const {
    tableCraftObjects: rawTableCraftObjects,
    tableCraftRows,
    tableCraftFunctions,
  } = useEditorTableCraftData();
  const [selectedObject, setSelectedObject] = useState<TableCraftObject>();
  const [filteredRows, setFilteredRows] = useState<
    TableCraftRow[] | undefined
  >();
  const [objectsSearchValue, setObjectsSearchValue] = useState('');
  const [rowsSearchValue, setRowsSearchValue] = useState('');
  const [isVisualSearch, setIsVisualSearch] = useState(false);
  const [visualSearchObjects, setVisualSearchObjects] = useState<{
    htmlElementString: string;
    referer?: string;
    results: TableCraftObject[] | { status: TableCraftObjectsStatus };
  }>({ htmlElementString: '', results: [] });
  const [isLoading, setIsLoading] = useState(false);
  const [modifiedFilter, setModifiedFilter] = useState<
    'all' | 'modified' | 'unmodified'
  >('all');
  const [alignValues, setAlignValues] = useState(true);

  const isIndexing = (result: {
    status: TableCraftObjectsStatus;
  }) => result.status === 'indexing';

  const tableCraftObjects = isIndexing(rawTableCraftObjects)
    ? []
    : rawTableCraftObjects.data;

  const visualBasedObjectsIds = (
    Array.isArray(visualSearchObjects.results as TableCraftObject[])
      ? (visualSearchObjects.results as TableCraftObject[])
      : []
  ).map((object) => object.structure_sha);
  const visualBasedObjects = tableCraftObjects.filter((object) =>
    visualBasedObjectsIds.includes(object.structure_sha)
  );
  const objectsByVisualSearch =
    isVisualSearch && typeof visualSearchObjects.results !== undefined
      ? isIndexing(
          visualSearchObjects.results as {
            status: TableCraftObjectsStatus;
          }
        )
        ? []
        : visualBasedObjects
      : tableCraftObjects;

  const objectsByFilter = (objectsByVisualSearch ?? []).filter(
    (object) =>
      (modifiedFilter === 'all' ||
        (modifiedFilter === 'modified' && object.is_modified) ||
        (modifiedFilter === 'unmodified' && !object.is_modified)) &&
      !object.hide
  );

  const initTableCraftObjects = async () => {
    editorSdk.getTableCraftFunctions();
    return editorSdk.setTableCraftObjects();
  };

  const refetchTableCraftObjects = async (searchValue?: string) => {
    const visualObjects = await editorSdk.visualSearch({
      htmlElementString: visualSearchObjects.htmlElementString,
      referer: visualSearchObjects.referer,
    });
    const objects = await editorSdk.setTableCraftObjects({
      searchQuery: searchValue ?? objectsSearchValue,
    });

    if (isVisualSearch) {
      setVisualSearchObjects({
        ...visualSearchObjects,
        results: visualObjects.data,
      });
    }

    return objects;
  };

  const openSingleObject = async (
    id: TableCraftObject['structure_sha'],
    objects?: TableCraftObject[]
  ) => {
    const skipSearchReset = id === selectedObject?.structure_sha;
    const objectsToUse =
      objects ?? (isVisualSearch ? visualBasedObjects : tableCraftObjects);
    try {
      const clickedObject = (objectsToUse as TableCraftObject[]).find(
        ({ structure_sha }) => structure_sha === id
      );
      setSelectedObject(clickedObject);
      await editorSdk.getTableCraftRows(id, {
        searchQuery: rowsSearchValue,
      });
      if (!skipSearchReset) setRowsSearchValue('');
      return clickedObject;
    } catch (e) {
      console.error('Error opening database table', e);
      notify.error('Error opening database table');
    }
  };

  const handleCellUpdate = async (data: CellUpdateData) => {
    try {
      await editorSdk.setTableCraftRow({
        objectId: data.objectId,
        column: data.column,
        value: data.value,
        row_id: data.row_id,
        id_column: data.id_column,
        enrich: alignValues,
      });
      await openSingleObject(selectedObject.structure_sha);
      await Promise.all([refetchTableCraftObjects(), editorSdk.reloadEditor()]);
    } catch (e) {
      console.error('Error setting database table row', e);
      notify.error('Error setting database table row');
    }
  };

  const toggleColumn = async (columnKey: string) => {
    setIsLoading(true);
    try {
      const filter_columns = getFilterColumns(
        selectedObject.filter_columns,
        columnKey
      );
      await handleUpdateSettings({ filter_columns });
    } catch (e) {
      console.error('Error hiding column', e);
      notify.error('Error hiding column');
    } finally {
      setIsLoading(false);
    }
  };

  const clearColumns = async () => {
    setIsLoading(true);
    try {
      await handleUpdateSettings({ filter_columns: [] });
    } catch (e) {
      console.error('Error clearing columns', e);
      notify.error('Error clearing columns');
    } finally {
      setIsLoading(false);
    }
  };

  const handleColumnUpdate = async ({
    objectId,
    columnKey,
    type,
    data,
  }: ColumnUpdateData) => {
    setIsLoading(true);
    try {
      if (type === 'AI') {
        const { originalColumnValues, prompt } = data as {
          originalColumnValues: string[];
          prompt: string;
        };
        let aiAssistData = originalColumnValues;
        if (alignValues) {
          aiAssistData = await editorSdk.getTableCraftEnrichmentColumns({
            objectId,
            col_name: columnKey,
          });
        }
        const newAIResponse = await editorSdk.aiAssist({
          task_type: assistType.FAKE_DATA_REPLACE_ASSIST,
          assist_data: { data: aiAssistData },
          prompt,
        });
        await editorSdk.setTableCraftColumn({
          objectId,
          col_name: columnKey,
          generator: 'demostack_custom_mapping',
          generator_args: {
            custom_mapping: JSON.stringify(newAIResponse.data),
          },
          enrich: alignValues,
        });
        await openSingleObject(selectedObject.structure_sha);
        await Promise.all([
          refetchTableCraftObjects(),
          editorSdk.reloadEditor(),
        ]);
      }

      if (type === 'hide') {
        await toggleColumn(data as string);
      }

      if (type === 'set-object-pk') {
        await handleUpdateSettings({
          set_object_pk: data as string,
        });
      }

      if (type === 'function') {
        const { selectedFn, params } = data as {
          selectedFn: TableCraftFunction;
          params: Record<string, boolean | string>;
        };
        const normalizedParams = getNormalizedParams(params);
        await editorSdk.setTableCraftColumn({
          objectId,
          col_name: columnKey,
          generator: selectedFn.name,
          generator_args: normalizedParams,
          enrich: alignValues,
        });
        await openSingleObject(selectedObject.structure_sha);
        await Promise.all([
          refetchTableCraftObjects(),
          editorSdk.reloadEditor(),
        ]);
      }
    } catch (e) {
      if (type === 'AI') {
        throw e;
      }
      console.error('Error updating database table column', e);
      notify.error('Error updating database table column');
    } finally {
      setIsLoading(false);
    }
  };

  const handleNavigate = async (tableId: string) => {
    setIsLoading(true);

    if (isVisualSearch) {
      await handleVisualSearchChange();
    } else {
      setIsVisualSearch(false);
    }

    try {
      setObjectsSearchValue('');
      const objects = await refetchTableCraftObjects('');
      const clickedObject = await openSingleObject(tableId, objects.data);
      if (clickedObject) {
        scrollToObject(clickedObject.structure_sha);
      }
    } catch (e) {
      console.error('Error navigating to table', e);
      notify.error('Error navigating to table');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateSettings = async ({
    display_name,
    filter_columns,
    hide,
    set_object_pk,
    object_id,
  }: UpdateSettingsData) => {
    const settings = {
      objectId: object_id ?? selectedObject.structure_sha,
      display_name: display_name ?? selectedObject.display_name,
      filter_columns: filter_columns ?? selectedObject.filter_columns,
      hide: hide ?? selectedObject.hide,
      set_object_pk: set_object_pk ?? selectedObject.id_column,
    };
    try {
      await editorSdk.setTableCraftSettings(settings);
      const objects = isVisualSearch
        ? await editorSdk.visualSearch({
            htmlElementString: visualSearchObjects.htmlElementString,
            referer: visualSearchObjects.referer,
          })
        : await editorSdk.setTableCraftObjects({
            searchQuery: objectsSearchValue,
          });
      if (isVisualSearch) {
        setVisualSearchObjects({
          ...visualSearchObjects,
          results: objects.data,
        });
      }
      if (selectedObject) {
        await openSingleObject(selectedObject.structure_sha, objects.data);
      }
    } catch (e) {
      console.error('Error updating database table settings', e);
      notify.error('Error updating database table settings');
    }
  };

  const handleObjectClick = async (id: TableCraftObject['structure_sha']) => {
    if (selectedObject !== undefined && selectedObject?.structure_sha === id) {
      return;
    }
    setIsLoading(true);
    try {
      await openSingleObject(id);
    } catch (e) {
      console.error('Error opening database table', e);
      notify.error('Error opening database table');
    } finally {
      setIsLoading(false);
    }
  };

  const resetState = () => {
    setSelectedObject(undefined);
    setFilteredRows(undefined);
    setRowsSearchValue('');
    setObjectsSearchValue('');
    setIsVisualSearch(false);
  };

  useEffect(() => {
    if (isOpen) {
      initTableCraftObjects();
      return;
    }
    resetState();
  }, [isOpen]);

  useEffect(() => {
    const filteredTableCraftRows = tableCraftRows.data.map((row) => {
      const data = excludeKeys(row.data, selectedObject?.filter_columns ?? []);
      return {
        ...row,
        data,
      };
    });

    if (!selectedObject || tableCraftRows.status === 'loading') {
      return;
    }

    setFilteredRows(filteredTableCraftRows);
  }, [rawTableCraftObjects.data, selectedObject, tableCraftRows.data]);

  const totalModifiedRows = (filteredRows ?? []).filter(
    (row) => row.modified_cols.length > 0
  ).length;

  const totalModifiedRowsLabel =
    totalModifiedRows === 1
      ? '1 database table'
      : `${totalModifiedRows} objects`;

  const totalModifiedCells = (filteredRows ?? []).reduce(
    (acc, row) => acc + row.modified_cols.length,
    0
  );

  const totalModifiedCellsLabel =
    totalModifiedCells === 1 ? '1 value' : `${totalModifiedCells} values`;

  const tableCraftObjectsStatus =
    (!isVisualSearch &&
      'status' in visualSearchObjects.results &&
      visualSearchObjects.results.status) ||
    rawTableCraftObjects.status;

  const handleRevert = async ({
    entity,
    row_id,
    col_name,
  }: {
    entity: 'cell' | 'column' | 'row';
    row_id?: string;
    col_name?: string;
  }) => {
    setIsLoading(true);
    try {
      await editorSdk.revertTableCraftModification({
        entity,
        objectId: selectedObject.structure_sha,
        row_id,
        col_name,
        enrich: alignValues,
      });
      await openSingleObject(selectedObject.structure_sha);
      await Promise.all([refetchTableCraftObjects(), editorSdk.reloadEditor()]);
    } catch (e) {
      console.error('Error reverting database table modification', e);
      notify.error('Error reverting database table modification');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRowSearchChange = (value: string) => {
    if (value === rowsSearchValue) return;
    setRowsSearchValue(value);
    editorSdk.getTableCraftRows(selectedObject.structure_sha, {
      searchQuery: value,
    });
  };

  const handleObjectsSearchChange = useCallback(
    async (value: string) => {
      try {
        setObjectsSearchValue(value);
        await editorSdk.setTableCraftObjects({
          searchQuery: value,
        });
      } catch (e) {
        console.error('Error database table search change', e);
        notify.error('Error database table search change');
      }
    },
    [editorSdk.setTableCraftObjects]
  );

  const handleVisualSearchChange = async () => {
    setIsVisualSearch(false);
    setVisualSearchObjects({
      htmlElementString: '',
      results: { status: 'loading' },
    });
    await editorSdk.setTableCraftObjects();
    setVisualSearchObjects({
      htmlElementString: '',
      results: [],
    });
  };

  const handleOnObjectHide = async (objectId: string, hide: boolean) => {
    setIsLoading(true);
    try {
      const clickedObject = tableCraftObjects.find(
        ({ structure_sha }) => structure_sha === objectId
      );
      await handleUpdateSettings({
        hide,
        object_id: objectId,
        display_name: clickedObject.display_name,
        filter_columns: clickedObject.filter_columns,
        set_object_pk: clickedObject.id_column,
      });
      // if we are removing the list that was selected, close the details
      if (selectedObject?.structure_sha === objectId) {
        setSelectedObject(undefined);
        setFilteredRows(undefined);
      }
      await refetchTableCraftObjects();
    } catch (e) {
      console.error('Error hiding object', e);
      notify.error('Error hiding object');
    } finally {
      setIsLoading(false);
    }
  };

  useEditorEvent(editorSdk, 'openVisualSearch', (payload) => {
    setVisualSearchObjects(payload);
    open();
    setIsVisualSearch(true);
    setSelectedObject(undefined);
  });

  // If the table craft objects are still indexing, refetch every 5 seconds
  useEffect(() => {
    if (rawTableCraftObjects.status === 'indexing') {
      const interval = setInterval(() => {
        refetchTableCraftObjects();
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [rawTableCraftObjects.status]);

  const filteredRowsTableKey = `${(filteredRows ?? [])
    .map((row) => row.id_value)
    .join('-')}-${rowsSearchValue}`;

  return (
    <ObjectsManager
      isOpen={isOpen}
      onClose={() => {
        close();
        setSelectedObject(undefined);
        setFilteredRows(undefined);
      }}
      searchValue={objectsSearchValue}
      onSearchChange={handleObjectsSearchChange}
      detailsContent={
        selectedObject ? (
          <ObjectDetails>
            <ObjectDetails.Header
              label={selectedObject.display_name}
              onTitleChange={(display_name) =>
                handleUpdateSettings({ display_name })
              }
            />
            <ObjectDetails.SubHeader label="Update the database table display name to make it easier to locate among all database tables." />
            <ObjectDetails.TopBar
              label={`This table displays all database tables of type “${selectedObject.display_name}“.`}
            >
              <div className="flex flex-col min-w-[230px]">
                <SearchBoxButton
                  key={selectedObject.structure_sha}
                  value={rowsSearchValue}
                  onChange={handleRowSearchChange}
                  aria-label={`Search ${selectedObject.display_name}`}
                />
              </div>
              <div className="w-px my-1.5 bg-ds-natural-150 self-stretch" />
              {selectedObject && (
                <div className="flex items-center gap-2.5">
                  <div>
                    <TooltipBubble
                      title="Hidden columns"
                      placement="top"
                    >
                      <HideOutlineIcon size={20} />
                    </TooltipBubble>
                  </div>
                  <div className="flex flex-col min-w-[180px]">
                    <MultiDropdownFilterContainer
                      key={selectedObject?.structure_sha}
                      selectedObject={selectedObject}
                      onToggleColumn={toggleColumn}
                      onClearColumns={clearColumns}
                    />
                  </div>
                </div>
              )}
            </ObjectDetails.TopBar>
            <ObjectDetails.Body>
              <ObjectsTable
                key={filteredRowsTableKey}
                objectId={selectedObject.structure_sha}
                rows={filteredRows ?? []}
                onCellUpdate={handleCellUpdate}
                onColumnUpdate={handleColumnUpdate}
                onNavigate={handleNavigate}
                onRevert={handleRevert}
                searchTerm={rowsSearchValue}
                disabledColumns={
                  selectedObject.id_column ? [selectedObject.id_column] : []
                }
                isLoading={isLoading || filteredRows === undefined}
                tableCraftFunctions={tableCraftFunctions.data}
              />
            </ObjectDetails.Body>
            <ObjectDetails.Footer
              modifiedCellsLabel={totalModifiedCellsLabel}
              modifiedRowsLabel={totalModifiedRowsLabel}
            />
          </ObjectDetails>
        ) : undefined
      }
      onCloseDetails={() => {
        setSelectedObject(undefined);
        setFilteredRows(undefined);
      }}
      selectedObject={selectedObject}
      rawTableCraftObjects={rawTableCraftObjects.data}
      tableCraftObjects={objectsByFilter}
      tableCraftObjectsStatus={tableCraftObjectsStatus}
      onObjectClick={handleObjectClick}
      isVisualSearch={isVisualSearch}
      onTextSearchClick={handleVisualSearchChange}
      modifiedFilter={modifiedFilter}
      onModifiedFilterChange={setModifiedFilter}
      alignValues={alignValues}
      onAlignValuesChange={() =>
        setAlignValues((prevAlignValues) => !prevAlignValues)
      }
      onObjectHide={handleOnObjectHide}
    />
  );
});

ObjectsTool.displayName = 'ObjectsTool';
